<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import { derived as sderived, type Readable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { toLuxon } from 'hydria/utils/time'

    import type { AdminUser, AuthLog, AutoToken, TwofaEntry } from './TwofaDefs'
    import type { User, UserHandler } from './user'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import TwofaAction from './TwofaAction.svelte'
    import TwofaReference from './TwofaReference.svelte'

    interface Props {
        close: () => void
    }

    let { close = () => {} }: Props = $props()

    const userh = getContext('User') as UserHandler
    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)

    let loading = $state(true)

    let entries: AuthLog[] = $state([])
    let autoTokens: { [key: string]: AutoToken } = $state({})
    let adminUsers: { [key: string]: AdminUser } = $state({})
    let twofaEntries: { [key: string]: TwofaEntry } = $state({})

    async function load() {
        loading = true
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${$user.id}/auth-log`)
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            console.error('Failed to load log:', r)
            notify({
                type: 'danger',
                title: '2FA',
                text: 'Erreur du chargement du journal.',
            })
            loading = false
            return
        }

        entries = []
        for (const e of r.data.entries) {
            e.stamp = toLuxon(e.stamp)

            entries.push(e)
        }

        autoTokens = {}
        for (const e of r.data.auto_tokens) {
            e.ctime = toLuxon(e.ctime)
            if (e.utilized_on) {
                e.utilized_on = toLuxon(e.utilized_on)
            }
            autoTokens[e.id] = e
        }

        adminUsers = {}
        for (const e of r.data.admin_users) {
            adminUsers[e.id] = e
        }

        twofaEntries = {}
        for (const e of r.data.twofa_entries) {
            e.ctime = toLuxon(e.ctime)
            for (const k of ['ctime', 'last_used', 'deleted_on']) {
                if (e[k]) e[k] = toLuxon(e[k])
            }
            twofaEntries[e.id] = e
        }

        loading = false
    }

    let clsOpen = false
    onMount(() => {
        load()
        if (!document.documentElement.classList.contains('modal-open')) {
            clsOpen = true
            document.documentElement.classList.add('modal-open')
        }
    })

    onDestroy(() => {
        if (clsOpen) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .modal-content {
        display: flex;
        flex-direction: column;
        height: 90vh;
    }
    .modal-body {
        flex: 1 0 0;
        overflow: auto;
    }
    .modal-body.mloading {
        flex: 0 0 auto;
    }

    .ip {
        font-feature-settings:
            'ss01' 1,
            'tnum' 1;
    }

    table.auth-log th:nth-child(1) {
        width: 10.25rem;
    }
    table.auth-log th:nth-child(2),
    table.auth-log td:nth-child(2) {
        width: 9rem;
        text-align: center;
    }
    table.auth-log th:nth-child(4),
    table.auth-log td:nth-child(4) {
        width: 2rem;
        text-align: center;
    }

    .modal-dialog {
        max-width: 74rem;
        width: 90vw;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            close()
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header text-bg-primary">
                <div>Journal d’authentification</div>
            </div>
            {#if loading}
                <div class="modal-body mloading">
                    <LoadingPattern pattern={[20, 30, 30, 30, 30]} />
                </div>
            {:else}
                <div class="modal-body">
                    <table class="table table-striped table-sm mb-0 auth-log">
                        <thead>
                            <tr>
                                <th>Stamp</th>
                                <th>IP</th>
                                <th>Action</th>
                                <th>Ok</th>
                                <th>Authentification connexe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each entries as entry}
                                <tr>
                                    <td><Date value={entry.stamp} format="h-table-s" /></td>
                                    <td class="ip">{entry.request_from_ip ? entry.request_from_ip : '–'}</td>
                                    <td><TwofaAction {entry} {twofaEntries} /></td>
                                    <td class:text-success={entry.success} class:text-danger={!entry.success}><Icon inline icon={entry.success ? 'mdi:checkbox-marked-outline' : 'mdi:checkbox-blank-outline'} /></td>
                                    <td><TwofaReference {entry} {autoTokens} {adminUsers} /></td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
            <div class="modal-footer text-bg-light">
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    onclick={() => {
                        close()
                    }}>{$_('Close')}</button
                >
            </div>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            close()
        }}
        aria-hidden="true"
    ></div>
</div>
